import { faRotate } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, type FormikHelpers } from "formik";
import type { AssetPosition } from "kubb";
import type React from "react";
import { useState } from "react";
import * as Yup from "yup";
import DateRangePicker from "../../../../../components/Forms/DateRangePicker/DateRangePicker";
import ConfirmModal from "../../../../../components/Modals/ConfirmModal";
import { Button } from "../../../../../components/Theme/button";
import {
  useResendAssetFilesForPeriod,
  useResendIntegrationFilesForPeriod,
} from "../../../../../hooks/createEntity";
import type { Asset } from "../../../../../utils/object";

interface Props {
  asset?: Asset;
  assetPosition?: AssetPosition;
}

interface FormValues {
  start_datetime: string;
  end_datetime: string;
  remove_quarantine: boolean;
}

const validationSchema = Yup.object().shape({
  start_datetime: Yup.string().required("Start date is required"),
  end_datetime: Yup.string().required("End date is required"),
});

const initialValues: FormValues = {
  start_datetime: "",
  end_datetime: "",
  remove_quarantine: false,
};

function BulkResendForm({ error }: { error?: Error }): React.ReactElement {
  return (
    <div className="flex flex-col gap-4">
      <DateRangePicker
        startDateName="start_datetime"
        endDateName="end_datetime"
        useStringValues={true}
        label={{ label: "Date range" }}
      />
      <label
        htmlFor="remove_quarantine"
        className="flex items-center gap-2 text-sm"
      >
        <Field
          id="remove_quarantine"
          type="checkbox"
          name="remove_quarantine"
          className="rounded"
        />
        Remove quarantine before resending
      </label>
      {error && <p className="text-sm text-red-600">Error: {error.message}</p>}
    </div>
  );
}

function BulkResendAssetFilesButton({
  asset,
}: { asset: Asset }): React.ReactElement {
  const [showModal, setShowModal] = useState(false);
  const resend = useResendAssetFilesForPeriod(asset);

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>,
  ) => {
    try {
      await resend.update({
        start_datetime: values.start_datetime,
        end_datetime: values.end_datetime,
        remove_quarantine: values.remove_quarantine,
      });
      setShowModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button outline size="sm" onClick={() => setShowModal(true)}>
        <FontAwesomeIcon icon={faRotate} className="mr-1.5" size="sm" />
        Resend files
      </Button>
      <ConfirmModal
        show={showModal}
        title="Resend files for period"
        buttonLabel={resend.loading ? "Resending..." : "Resend"}
        disabled={resend.loading}
        body={<BulkResendForm error={resend.error} />}
        handleClose={() => setShowModal(false)}
        handleAction={() => {}}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      />
    </>
  );
}

function BulkResendIntegrationFilesButton({
  assetPosition,
}: { assetPosition: AssetPosition }): React.ReactElement {
  const [showModal, setShowModal] = useState(false);
  const resend = useResendIntegrationFilesForPeriod(assetPosition);

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>,
  ) => {
    try {
      await resend.update({
        start_datetime: values.start_datetime,
        end_datetime: values.end_datetime,
        remove_quarantine: values.remove_quarantine,
      });
      setShowModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button outline size="sm" onClick={() => setShowModal(true)}>
        <FontAwesomeIcon icon={faRotate} className="mr-1.5" size="sm" />
        Resend files
      </Button>
      <ConfirmModal
        show={showModal}
        title="Resend files for period"
        buttonLabel={resend.loading ? "Resending..." : "Resend"}
        disabled={resend.loading}
        body={<BulkResendForm error={resend.error} />}
        handleClose={() => setShowModal(false)}
        handleAction={() => {}}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      />
    </>
  );
}

export default function BulkResendFilesButton({
  asset,
  assetPosition,
}: Props): React.ReactElement {
  if (asset) {
    return <BulkResendAssetFilesButton asset={asset} />;
  }
  if (assetPosition) {
    return <BulkResendIntegrationFilesButton assetPosition={assetPosition} />;
  }
  return <></>;
}
